import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import {
  ChevronRight as ChevronRightIcon,
  ExpandMore,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

// Active nav item uses the primary color (text + icon + a light primary tint),
// matching the previous Toolpad styling.
const selectedSx = (theme) => ({
  "&.Mui-selected": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    // The icon and the label Typography (ListItemText and the mini caption);
    // the label is targeted explicitly because the theme forces a black text
    // color on all Typography variants.
    "& .MuiListItemIcon-root": { color: theme.palette.primary.main },
    "& .MuiTypography-root": { color: theme.palette.primary.main },
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
  },
});

import { useAuth } from "@/features/authentication";
import { useIncomingPurchasePaymentsCount } from "@/features/purchase-payments";
import { useIncomingPurchaseReturnsCount } from "@/features/purchase-returns";
import { useIncomingSalesInvoicesCount } from "@/features/sales-invoices";

import { BRANDING } from "@/branding";
import { NAVIGATION } from "@/navigation";

const DRAWER_WIDTH = 320;
const MINI_WIDTH = 84;
const COLLAPSE_KEY = "sidebarCollapsed";

// Recursively drop navigation entries the user lacks permission for, and any
// parent left with no visible children.
const filterNavigation = (items, hasPermission) =>
  items
    .map((item) => {
      const children = item.children
        ? filterNavigation(item.children, hasPermission)
        : null;
      const allowed = !item.permission || hasPermission(item.permission);
      if (allowed && (!item.children || (children && children.length > 0))) {
        return { ...item, children };
      }
      return null;
    })
    .filter(Boolean);

// A group may omit `segment` to group visually without owning a URL segment —
// its children then resolve against the grandparent's path, so grouping items
// in the drawer never forces a route change.
const resolvePath = (item, basePath) =>
  item.segment ? `${basePath}/${item.segment}` : basePath;

const isItemActive = (item, basePath, pathname) => {
  const path = resolvePath(item, basePath);
  // A segment-less group owns no path of its own, so it is active only when one
  // of its descendants is (a prefix match would light it up for every sibling).
  if (!item.segment) {
    return (item.children ?? []).some((child) =>
      isItemActive(child, path, pathname),
    );
  }
  return pathname === path || pathname.startsWith(`${path}/`);
};

// Counts are declared on nav items by key (`badge: "incomingPurchaseReturns"`)
// so navigation.jsx stays a static tree; the shell supplies the numbers.
const resolveBadgeCount = (item, badges) =>
  (item.badge ? (badges[item.badge] ?? 0) : 0) +
  (item.children ?? []).reduce(
    (total, child) => total + resolveBadgeCount(child, badges),
    0,
  );

/**
 * Single recursive nav renderer used for the expanded drawer, the collapsed
 * mini rail, and the hover flyout. The `mini` prop switches each item's layout
 * WITHOUT swapping component types, so collapsing/expanding only re-renders
 * (reconciles) the tree instead of unmounting+remounting it — the key to a
 * smooth transition (this mirrors Toolpad's isMini-prop approach).
 */
const NavList = ({
  items,
  basePath,
  depth,
  mini,
  expanded,
  badges,
  onToggle,
  onNavigate,
  onOpenFlyout,
  onScheduleClose,
  pathname,
}) => (
  <List disablePadding component="div">
    {items.map((item) => {
      const fullPath = resolvePath(item, basePath);
      // Segment-less groups share their parent's path, so the expand/active
      // state needs a key of its own.
      const stateKey = item.segment ? fullPath : `${basePath}#${item.title}`;
      const hasChildren = item.children && item.children.length > 0;
      const active = isItemActive(item, basePath, pathname);
      const open = expanded[stateKey] ?? active;
      const badgeCount = resolveBadgeCount(item, badges);

      const buttonSx = mini
        ? {
            position: "relative",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            mx: 0.75,
            my: 0.25,
            py: 1,
            borderRadius: 2,
          }
        : { mx: 1, my: 0.25, borderRadius: 2, pl: 1.5 + depth * 1.5 };

      // A collapsed group stands in for its children, so it carries their
      // combined count; once it is open each child shows its own.
      const showBadge = badgeCount > 0 && (mini || !hasChildren || !open);

      const inner = mini ? (
        <>
          {hasChildren ? (
            <ChevronRightIcon
              sx={{
                position: "absolute",
                top: "50%",
                right: 4,
                transform: "translateY(-50%)",
                fontSize: 16,
                opacity: 0.5,
              }}
            />
          ) : null}
          <ListItemIcon sx={{ minWidth: 0 }}>
            {showBadge ? (
              <Badge
                badgeContent={badgeCount}
                sx={{
                  "& .MuiBadge-badge": {
                    bgcolor: "accent.main",
                    color: "accent.contrastText",
                    fontWeight: 600,
                  },
                }}
              >
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </ListItemIcon>
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              lineHeight: 1.4,
              textAlign: "center",
              maxWidth: MINI_WIDTH - 16,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.title}
          </Typography>
        </>
      ) : (
        <>
          <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.title} />
          {showBadge ? (
            <Chip
              label={badgeCount}
              size="small"
              sx={{
                height: 20,
                mr: hasChildren ? 0.5 : 0,
                bgcolor: "accent.main",
                // The theme forces black on every Typography variant, so the
                // label needs the color set on it, not just on the Chip.
                "& .MuiChip-label": {
                  color: "accent.contrastText",
                  fontWeight: 600,
                  px: 0.75,
                },
              }}
            />
          ) : null}
          {hasChildren ? (
            open ? (
              <ExpandMore sx={{ color: "action.active" }} />
            ) : (
              <ChevronRightIcon sx={{ color: "action.active" }} />
            )
          ) : null}
        </>
      );

      if (hasChildren) {
        return (
          <Box key={stateKey}>
            <ListItemButton
              selected={mini ? active : false}
              onClick={mini ? undefined : () => onToggle(stateKey)}
              onMouseEnter={mini ? (event) => onOpenFlyout(event, item) : undefined}
              onMouseLeave={mini ? onScheduleClose : undefined}
              sx={[buttonSx, selectedSx]}
            >
              {inner}
            </ListItemButton>
            {!mini ? (
              <Collapse in={open} timeout="auto" unmountOnExit>
                <NavList
                  items={item.children}
                  basePath={fullPath}
                  depth={depth + 1}
                  mini={false}
                  expanded={expanded}
                  badges={badges}
                  onToggle={onToggle}
                  onNavigate={onNavigate}
                  onOpenFlyout={onOpenFlyout}
                  onScheduleClose={onScheduleClose}
                  pathname={pathname}
                />
              </Collapse>
            ) : null}
          </Box>
        );
      }

      return (
        <ListItemButton
          key={fullPath}
          component={RouterLink}
          to={fullPath}
          selected={active}
          onClick={onNavigate}
          sx={[buttonSx, selectedSx]}
        >
          {inner}
        </ListItemButton>
      );
    })}
  </List>
);

NavList.propTypes = {
  items: PropTypes.array.isRequired,
  basePath: PropTypes.string.isRequired,
  depth: PropTypes.number.isRequired,
  mini: PropTypes.bool.isRequired,
  expanded: PropTypes.object.isRequired,
  badges: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onOpenFlyout: PropTypes.func.isRequired,
  onScheduleClose: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
};

export const AppShell = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, hasPermission, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === "true",
  );
  const [expanded, setExpanded] = useState({});
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [flyout, setFlyout] = useState({ anchorEl: null, item: null });
  const closeTimer = useRef();

  const navigation = useMemo(
    () => filterNavigation(NAVIGATION, hasPermission),
    [hasPermission],
  );

  const incomingPurchasePaymentsCount = useIncomingPurchasePaymentsCount();
  const incomingPurchaseReturnsCount = useIncomingPurchaseReturnsCount();
  const incomingSalesInvoicesCount = useIncomingSalesInvoicesCount();
  const badges = useMemo(
    () => ({
      incomingPurchasePayments: incomingPurchasePaymentsCount,
      incomingPurchaseReturns: incomingPurchaseReturnsCount,
      incomingSalesInvoices: incomingSalesInvoicesCount,
    }),
    [
      incomingPurchasePaymentsCount,
      incomingPurchaseReturnsCount,
      incomingSalesInvoicesCount,
    ],
  );

  const miniMode = isDesktop && collapsed;
  const permanentWidth = miniMode ? MINI_WIDTH : DRAWER_WIDTH;

  // Close the hover flyout whenever the route changes.
  useEffect(() => {
    setFlyout({ anchorEl: null, item: null });
  }, [pathname]);

  const handleToggle = (path) =>
    setExpanded((prev) => ({ ...prev, [path]: !(prev[path] ?? false) }));

  const handleMenuButton = () => {
    if (isDesktop) {
      setCollapsed((prev) => {
        localStorage.setItem(COLLAPSE_KEY, String(!prev));
        return !prev;
      });
    } else {
      setMobileOpen(true);
    }
  };

  const handleSignOut = () => {
    setAccountAnchor(null);
    logout();
    navigate("/", { replace: true });
  };

  const openFlyout = (event, item) => {
    clearTimeout(closeTimer.current);
    setFlyout({ anchorEl: event.currentTarget, item });
  };
  const scheduleCloseFlyout = () => {
    closeTimer.current = setTimeout(
      () => setFlyout({ anchorEl: null, item: null }),
      200,
    );
  };
  const cancelCloseFlyout = () => clearTimeout(closeTimer.current);
  const closeFlyout = () => setFlyout({ anchorEl: null, item: null });

  const widthTransition = theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

  const drawerContent = (
    // Fixed width = target state so the inner layout snaps to its final form
    // immediately; the animating paper (overflowX hidden) clips/reveals it,
    // avoiding a reflow during the width transition.
    (<Box sx={{ height: "100%", width: permanentWidth, overflowY: "auto" }}>
      <Toolbar />
      <Box sx={{ height: 12 }} />
      <NavList
        items={navigation}
        basePath=""
        depth={0}
        mini={miniMode}
        expanded={expanded}
        badges={badges}
        onToggle={handleToggle}
        onNavigate={() => !isDesktop && setMobileOpen(false)}
        onOpenFlyout={openFlyout}
        onScheduleClose={scheduleCloseFlyout}
        pathname={pathname}
      />
      <Box sx={{ height: 24 }} />
    </Box>)
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleMenuButton}
            aria-label="toggle navigation"
          >
            {isDesktop && !collapsed ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mr: 2,
              "& img": { height: 36, width: "auto", display: "block" },
            }}
          >
            {BRANDING.logo}
            <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>
              {BRANDING.title}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            onClick={(event) => setAccountAnchor(event.currentTarget)}
            size="small"
            aria-label="account menu"
          >
            <Avatar sx={{ width: 32, height: 32 }} />
          </IconButton>
          <Menu
            anchorEl={accountAnchor}
            open={Boolean(accountAnchor)}
            onClose={() => setAccountAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">
                {user?.name}
                {user?.role?.name ? ` (${user.role.name})` : ""}
              </Typography>
              {user?.email ? (
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  {user.email}
                </Typography>
              ) : null}
            </Box>
            <Divider />
            <MenuItem onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Keluar
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { md: permanentWidth },
          flexShrink: { md: 0 },
          transition: widthTransition,
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              borderRight: "none",
            },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: permanentWidth,
              borderRight: "none",
              overflowX: "hidden",
              transition: widthTransition,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>
      {/* Hover flyout for collapsed groups */}
      <Popover
        open={Boolean(flyout.anchorEl)}
        anchorEl={flyout.anchorEl}
        onClose={closeFlyout}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        sx={{ pointerEvents: "none" }}
        slotProps={{
          paper: {
            onMouseEnter: cancelCloseFlyout,
            onMouseLeave: scheduleCloseFlyout,
            // Transparent wrapper whose left padding is an invisible "bridge"
            // spanning the gap to the rail, so diagonal mouse travel from the
            // item into the menu never crosses a dead zone (Toolpad's trick).
            sx: {
              pointerEvents: "auto",
              bgcolor: "transparent",
              backgroundImage: "none",
              boxShadow: "none",
              overflow: "visible",
              pl: "12px",
            },
          },
        }}
      >
        {flyout.item ? (
          <Paper elevation={3} sx={{ minWidth: 230 }}>
            <Box sx={{ py: 1 }}>
              <NavList
                items={flyout.item.children}
                basePath={`/${flyout.item.segment}`}
                depth={0}
                mini={false}
                expanded={expanded}
                badges={badges}
                onToggle={handleToggle}
                onNavigate={closeFlyout}
                onOpenFlyout={openFlyout}
                onScheduleClose={scheduleCloseFlyout}
                pathname={pathname}
              />
            </Box>
          </Paper>
        ) : null}
      </Popover>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${permanentWidth}px)` },
          minWidth: 0,
          bgcolor: "background.default",
          minHeight: "100vh",
          transition: widthTransition,
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};
